import { combineLatest } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnInit,
  Optional,
  PLATFORM_ID,
  ViewEncapsulation,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { IAccountInformation } from '~shared/models';
import { AccountFlyoutFailoverService } from '~app/modules/generic/components/header/account-flyout-failover/account-flyout-failover.service';
import { EntriesEntity } from '~app/modules/generic/models/account-flyout-reponse.model';
import { AuthService } from '~app/modules/authorization/services/authorization-idam.service';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { BlockRedirectionService } from '~shared/services/block-redirection.service';
import { isPlatformBrowser } from '@angular/common';
import { isNotSSO, redirectTo, toggleFlyoutEventKey } from '~server/utils/auth.utils';
import { AuthorizationHttpService } from '~app/modules/authorization/services/authorization-http.service';
import { environment } from '~env/environment';
import { IS_COMPANION } from '~app/app.module';

let isListenerAdded = false;

@Component({
  selector: 'app-account-flyout-failover',
  templateUrl: './account-flyout-failover.component.html',
  styleUrls: ['./account-flyout-failover.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AccountFlyoutFailoverComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  user: IAccountInformation;

  @Input() isMobileView: boolean;

  isFlyoutOpened = false;
  showSSONotice = true;
  isSecondaryHeaderTrigger = false;
  private submenuEntries: EntriesEntity[] | null = null;
  isContextsMenuOpened = false;
  isSubmenuOpened = false;
  accountFlyoutData$ = combineLatest([
    this.accountFlyoutFailoverService.accountFlyoutData$.asObservable(),
    this.accountFlyoutFailoverService.selectedContext$.asObservable(),
  ]).pipe(
    map(([accountFlyoutData, selectedContext]) => ({
      ...accountFlyoutData,
      selectedContext,
    }))
  );

  private lastActiveElement: HTMLElement | null = null;
  private focusableElements: HTMLElement[] = [];

  constructor(
    private readonly accountFlyoutFailoverService: AccountFlyoutFailoverService,
    private readonly authService: AuthService,
    private eRef: ElementRef,
    private accountInfoService: AccountInfoService,
    private blockRedirectionService: BlockRedirectionService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private authorizationHttpService: AuthorizationHttpService,

    @Optional() @Inject(IS_COMPANION) public isCompanion,
    @Inject(DOCUMENT) private document: Document
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.showSSONotice = isNotSSO();
    }
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event) {
    if (!this.eRef.nativeElement.contains(event.target) && !this.isSecondaryHeaderTrigger) {
      this.toggleFlyout(false);
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapePressed(event: KeyboardEvent) {
    if (this.isFlyoutOpened) {
      this.toggleFlyout(false);
      event.preventDefault();
    }
  }

  // Handle keyboard arrow navigation
  @HostListener('keydown.arrowdown', ['$event'])
  onArrowDown(event: KeyboardEvent) {
    if (this.isFlyoutOpened) {
      this.moveFocus(1);
      event.preventDefault();
    }
  }

  @HostListener('keydown.arrowup', ['$event'])
  onArrowUp(event: KeyboardEvent) {
    if (this.isFlyoutOpened) {
      this.moveFocus(-1);
      event.preventDefault();
    }
  }

  // Helper method for keyboard navigation
  private moveFocus(direction: number) {
    if (!this.isFlyoutOpened) return;

    // Get all focusable elements in the flyout
    this.updateFocusableElements();

    // Find the index of the currently focused element
    const activeElement = document.activeElement as HTMLElement;
    const currentIndex = this.focusableElements.indexOf(activeElement);

    // Calculate the new index, wrapping around if needed
    let newIndex = currentIndex + direction;

    if (newIndex < 0) {
      newIndex = this.focusableElements.length - 1;
    } else if (newIndex >= this.focusableElements.length) {
      newIndex = 0;
    }

    // Focus the new element
    this.focusableElements[newIndex]?.focus();
  }

  private updateFocusableElements() {
    // Get all focusable elements within the flyout
    const selector = 'a[href], button, [tabindex]:not([tabindex="-1"]), [role="button"], [role="menuitem"]';
    const flyout = this.eRef.nativeElement.querySelector('.account-flyout.open');

    if (flyout) {
      this.focusableElements = Array.from(flyout.querySelectorAll(selector)) as HTMLElement[];
    }
  }

  toggle = () => {
    this.isSecondaryHeaderTrigger = true;
    this.toggleFlyout();
  };

  ngOnInit(): void {
    this.accountFlyoutFailoverService.loadAccountFlyoutData();
    if (!isListenerAdded) {
      window.addEventListener(toggleFlyoutEventKey, this.toggle);
      isListenerAdded = true;
    }
  }

  ngAfterViewInit(): void {
    // Initialize focusable elements
    this.updateFocusableElements();
  }

  ngOnDestroy(): void {
    // Remove global event listener
    if (isListenerAdded) {
      window.removeEventListener(toggleFlyoutEventKey, this.toggle);
      isListenerAdded = false;
    }
  }

  handleCSSLinks(event, redirectionURL) {
    if (!redirectionURL) {
      return;
    }
    const urlOrigin = new URL(redirectionURL).origin;
    const myMetroURL = environment.myMetroBaseUrl();

    if (urlOrigin === myMetroURL) {
      event.preventDefault();
      this.authorizationHttpService.getLoginLink(redirectionURL).subscribe((res: { loginLink: string }) => {
        if (res?.loginLink) {
          redirectTo(res.loginLink);
        } else {
          redirectTo(redirectionURL);
        }
      });
      return false;
    }
    redirectTo(redirectionURL);
  }

  toggleFlyout(opened?: boolean) {
    const wasOpen = this.isFlyoutOpened;
    this.isFlyoutOpened = opened !== undefined ? opened : !this.isFlyoutOpened;

    if (this.accountFlyoutFailoverService.accountFlyoutData$.value) {
      if (!wasOpen && this.isFlyoutOpened) {
        // Save the current active element to restore focus later
        this.lastActiveElement = document.activeElement as HTMLElement;

        // Set focus to the first focusable element when opening
        setTimeout(() => {
          this.updateFocusableElements();
          if (this.focusableElements.length > 0) {
            this.focusableElements[0].focus();
          }
        }, 50);
      } else if (wasOpen && !this.isFlyoutOpened) {
        // Close nested menus
        this.closeSubmenu();
        this.isContextsMenuOpened = false;

        // Restore focus to the element that was active before opening
        if (this.lastActiveElement) {
          setTimeout(() => {
            this.lastActiveElement?.focus();
            this.lastActiveElement = null;
          }, 50);
        }
      }
    }
  }

  openSubmenu(entries: EntriesEntity[]) {
    this.submenuEntries = entries;
    this.isSubmenuOpened = true;
    setTimeout(() => {
      const firstSubmenuItem = this.eRef.nativeElement.querySelector('.submenu-info a');
      firstSubmenuItem?.focus();
    }, 50);
  }
  closeSubmenu(event?: Event) {
    if (event) {
      event.preventDefault();
    }
    this.submenuEntries = null;
    this.selectFirstMainMenuItem();
    return false;
  }

  private selectFirstMainMenuItem() {
    // Focus the first item in the main menu
    setTimeout(() => {
      const firstMainMenuItem = this.eRef.nativeElement.querySelector(
        '.menusection .menuentry-link a, .menusection .menuentry-link .menuentry'
      );
      firstMainMenuItem?.focus();
    }, 50);
  }

  toggleContextsMenu() {
    this.isContextsMenuOpened = !this.isContextsMenuOpened;
    if (this.isContextsMenuOpened) {
      setTimeout(() => {
        const firstContextItem = this.eRef.nativeElement.querySelector('.secondary-account-info');
        firstContextItem?.focus();
      }, 50);
    } else {
      this.selectFirstMainMenuItem();
    }
  }

  get userName(): string {
    return this.user?.firstName?.concat(this.user?.lastName ? ` ${this.user?.lastName}` : '') || this.user?.email;
  }

  logout(e: Event) {
    e.preventDefault();

    this.authService.logout();
    this.isFlyoutOpened = false;
  }

  switchContext(id: string) {
    this.authService
      .switchContext(id)
      .pipe(
        switchMap(() =>
          combineLatest([
            this.accountInfoService.isAccountBlocked(),
            this.accountInfoService.isSplitPaymentObliged(),
          ]).pipe(take(1))
        )
      )
      .subscribe(([blocked, obliged]) => {
        this.isFlyoutOpened = false;
        this.isContextsMenuOpened = false;
        this.accountFlyoutFailoverService.selectContext(id);
        this.blockRedirectionService.redirect(blocked, obliged, id);
        window.dispatchEvent(
          new CustomEvent('cms:user-profile:active-context-change', {
            detail: id,
          })
        );
      });
  }

  // Add aria-live announcement for screen readers
  announceForScreenReader(message: string) {
    // Create or get the aria-live region
    let liveRegion = this.document.getElementById('account-flyout-status');
    if (!liveRegion) {
      liveRegion = this.document.createElement('div');
      liveRegion.id = 'account-flyout-status';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.classList.add('sr-only'); // add CSS for screen reader only
      this.document.body.appendChild(liveRegion);
    }

    // Set the message
    liveRegion.textContent = message;

    // Clear it after a short delay
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 1000);
  }

  toggleSubmenu() {
    this.isSubmenuOpened = !this.isSubmenuOpened;
    if (this.isSubmenuOpened) {
      setTimeout(() => {
        const firstSubmenuItem = this.eRef.nativeElement.querySelector('.submenu-info a');
        firstSubmenuItem?.focus();
      }, 50);
    }
  }
}
